/**
 * <keystone-field-money> \u2014 monetary input.
 *
 * Maps onto JSON Schema `type: "integer"` + custom `x-format: "money"`.
 * The wire shape is integer cents (matches what the PO schema's
 * `total_cents` field expects); the display shape is decimal dollars
 * with two decimal places. This widget is the bridge between the two.
 *
 * Contract
 * --------
 *   Internal `value` (and emitted in `keystone-field-input` events):
 *     the cents amount as a string ("12345" \u2261 $123.45). Always an
 *     integer string; empty when the field is empty.
 *
 *   Display value (what the user sees and types):
 *     dollars with optional decimals. Accepts "1234", "1234.5",
 *     "1234.56", "1,234.56". Blur re-formats to canonical "1234.56".
 *
 *   Currency symbol:
 *     visual prefix only, never part of the input value. Defaults to
 *     "$"; can be overridden via the `currency-symbol` attribute (the
 *     schema's `x-currency` hint is mapped to a symbol upstream by
 *     <keystone-form>).
 *
 * Out of scope
 * ------------
 *   - Locale-specific thousand-separator formatting (we strip them on
 *     input but don't insert them on display).
 *   - Currencies with non-2dp minor units (JPY = 0dp, BHD = 3dp). Add
 *     a `precision` attribute when needed.
 */

import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { fieldStyles, humaniseFieldName } from "./base.js";

/**
 * Parse a user-entered dollar string into cents. Strips commas and
 * whitespace; returns null for empty input, NaN for unparseable input.
 *
 *   "1234"      \u2192 123400
 *   "1234.56"   \u2192 123456
 *   "1,234.5"   \u2192 123450
 *   ""          \u2192 null
 *   "abc"       \u2192 NaN
 *
 * Rounded half-up for hundredths to avoid the 0.1 + 0.2 = 0.30000... trap.
 */
export function dollarsToCents(input: string): number | null {
  const trimmed = input.trim().replace(/[,\s]/g, "");
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return Number.NaN;
  return Math.round(n * 100);
}

/**
 * Format a cents string as a 2dp dollar string for display.
 *
 *   "12345" \u2192 "123.45"
 *   "100"   \u2192 "1.00"
 *   ""      \u2192 ""
 *   "abc"   \u2192 ""   (defensive; shouldn't happen via the widget)
 */
export function centsToDollarsDisplay(cents: string): string {
  if (cents === "") return "";
  const n = parseInt(cents, 10);
  if (!Number.isFinite(n)) return "";
  const dollars = n / 100;
  return dollars.toFixed(2);
}

@customElement("keystone-field-money")
export class KeystoneFieldMoney extends LitElement {
  static override styles = [
    fieldStyles,
    css`
      .money-wrap {
        position: relative;
      }
      .money-prefix {
        position: absolute;
        left: 0.625rem;
        top: 50%;
        transform: translateY(-50%);
        color: #6b7280; /* tw gray-500 */
        pointer-events: none;
        font-size: 1rem;
      }
      .money-wrap input {
        padding-left: 1.5rem;
      }
    `,
  ];

  @property({ type: String }) name = "";
  @property({ type: String }) label = "";
  /** Cents as a string. Internal canonical value. */
  @property({ type: String }) value = "";
  @property({ type: Boolean }) required = false;
  @property({ type: String }) error: string | null = null;
  @property({ type: Boolean }) disabled = false;

  @property({ attribute: "currency-symbol", type: String }) currencySymbol =
    "$";

  /**
   * What the user sees in the <input> as they type. Independent of
   * `value` (cents) so the user can type freely without us rewriting
   * mid-keystroke; we re-derive on blur.
   */
  private displayValue: string = "";
  private hasFocus = false;

  override willUpdate(changed: Map<string, unknown>): void {
    // When `value` (cents) changes from the outside (parent form
    // updating from schema-driven seed, or a server-side reset), sync
    // the display. We don't touch displayValue while the user has
    // focus so we never overwrite their in-flight typing.
    if (changed.has("value") && !this.hasFocus) {
      this.displayValue = centsToDollarsDisplay(this.value);
    }
  }

  override updated(): void {
    if (this.error) this.setAttribute("data-error", "");
    else this.removeAttribute("data-error");
  }

  private onInput(e: Event): void {
    const input = e.target as HTMLInputElement;
    this.displayValue = input.value;
    const cents = dollarsToCents(input.value);
    // NaN \u2192 user is mid-typing something invalid (e.g. "12a"). Keep the
    // existing internal cents value; the form will surface the issue at
    // submit if it isn't fixed.
    let nextValue = this.value;
    if (cents === null) nextValue = "";
    else if (Number.isFinite(cents)) nextValue = String(cents);

    if (nextValue !== this.value) {
      this.value = nextValue;
      this.dispatchEvent(
        new CustomEvent("keystone-field-input", {
          detail: { name: this.name, value: nextValue },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  private onFocus(): void {
    this.hasFocus = true;
  }

  private onBlur(): void {
    this.hasFocus = false;
    // Re-derive display from canonical cents on blur so the user sees
    // tidy "123.45" even if they typed "123.4" or "00123.450".
    this.displayValue = centsToDollarsDisplay(this.value);
    this.requestUpdate();
  }

  override render() {
    const label = this.label || humaniseFieldName(this.name);
    const inputId = `input-${this.name}`;

    return html`
      <label for=${inputId}>
        ${label}${this.required
          ? html`<span class="required-marker" aria-hidden="true">*</span>`
          : ""}
      </label>
      <div class="money-wrap">
        <span class="money-prefix" aria-hidden="true"
          >${this.currencySymbol}</span
        >
        <input
          id=${inputId}
          name=${this.name}
          type="text"
          inputmode="decimal"
          .value=${this.displayValue}
          ?required=${this.required}
          ?disabled=${this.disabled}
          aria-invalid=${this.error ? "true" : "false"}
          aria-describedby=${this.error ? `${inputId}-error` : ""}
          @input=${this.onInput}
          @focus=${this.onFocus}
          @blur=${this.onBlur}
        />
      </div>
      ${this.error
        ? html`<p class="error" id="${inputId}-error" role="alert">
            ${this.error}
          </p>`
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "keystone-field-money": KeystoneFieldMoney;
  }
}
